// @ts-strict-ignore
import React, {
  useEffect, useMemo, useState,
  useContext,
} from 'react';
import { UserContext } from '~/dashboard/context';
import {
  Box,
  TextField,
  MultiSelect,
  Separator,
  useSnackBar,
  HelpText,
} from '@usealloy/component-library';
import type { FieldErrors } from 'react-hook-form';
import {
  Controller, useFormContext, useFieldArray,
} from 'react-hook-form';

import { getActionName } from 'flow/service/actions';
import type { FormState, ServiceOption, DisplayNameOption } from './types';
import apiCalls from '~/helpers/apiCalls';
import { convertServiceConfigOption } from '../serviceLocalSettingHelpers';
import {
  getServiceFailureDefaultOptions, convertActionOptionsToServiceOptions,
} from '../serviceOptionHelpers';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { setServiceActionOptionPublicIdInEditing, updateServiceActionValidation, updateServiceConfigInWorkflowProps } from 'flow/store/redux/slice';
import {
  selectIsOnboardingWorkflow, selectFocusedAction, selectRecurringWorkflows, selectServices,
  selectWorkflowFailureHandlingMode,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { ServiceConfigOption } from '~/dashboard/views/settings/service-configuration/ServiceConfigOptionView';
import type { FormattedServiceConfigOption, ServiceConfig } from '~/dashboard/views/settings/service-configuration/types';
import { DefaultLevel, ServiceConfigModalMode } from '~/dashboard/views/settings/service-configuration/types';

import { generatePublicId } from '~/dashboard/views/workflows/store/engine/utils';
import {
  StyledLocalSettingContainer,
} from './styles';
import { DISPLAY_NAME_OPTION_KEY, WEBHOOK_KEY } from '../constants';
import RecurringWebhookContainer from '../ServiceOptionsTab/Sections/RecurringWebhook';
import type { NodeEditorAutoSaveTabProps } from '../../useNodeEditorForm/types';
import { useDebouncedFunction } from '../../useNodeFormAutoSave';
import { failureHandlingDescriptionBestEffortMode, failureHandlingDescriptionFullThresholdMode } from '../../constants';

export default function ServiceLocalSettingsTab({
  handleAutoSave,
}: NodeEditorAutoSaveTabProps) {
  const addSnackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);
  const isOnboardingWorkflow = useAppSelector(selectIsOnboardingWorkflow);
  const recurringWorkflowsByService = useAppSelector(selectRecurringWorkflows);
  const failureHandlingModeValue = useAppSelector(selectWorkflowFailureHandlingMode);
  const services = useAppSelector(selectServices);
  const { typhonDomain } = useContext(UserContext);
  const service = Object.values(
    services,
  ).find(serviceObj => (serviceObj.internal_name === focusedAction.service.internalName));
  const {
    service_id: serviceId,
    serviceConfig: initServiceConfig,
    production_enabled: productionEnabled,
    sandbox_enabled: sandboxEnabled,
  } = service;

  const [serviceConfig, setServiceConfig] = useState<ServiceConfig>(initServiceConfig);
  const [isServiceDataFetched, setIsServiceDataFetched] = useState<boolean>();
  const [isAddingOption, setIsAddingOption] = useState(false);

  const serviceFailureOptions = useMemo(() => getServiceFailureDefaultOptions(), []);
  const serviceOptions = useMemo(
    () => focusedAction && convertActionOptionsToServiceOptions(focusedAction.options),
    [focusedAction],
  );

  useEffect(() => {
    const fetchServiceData = async () => {
      if (!serviceConfig) {
        const serviceConfigData: ServiceConfig = await apiCalls.settings.getServiceConfig(
          { typhonDomain, serviceId },
        );

        dispatch(updateServiceConfigInWorkflowProps({
          serviceConfig: serviceConfigData,
          internalName: focusedAction?.service.internalName,
        }));

        setServiceConfig(serviceConfigData);
      }
      setIsServiceDataFetched(true);
    };

    fetchServiceData();
  }, [typhonDomain, serviceId, focusedAction, serviceConfig, dispatch]);

  const {
    control,
    setValue,
    watch,
    trigger,
    handleSubmit,
    formState: { errors },
  } = useFormContext<FormState>();
  const {
    append: appendServiceConfigOption,
    fields: formattedServiceConfigOptions,
    remove: removeServiceConfigOption,
    replace: replaceServiceConfigOption,
  } = useFieldArray({
    name: 'formattedServiceConfigOptions',
    control,
  });

  const watchFieldArray = watch('formattedServiceConfigOptions');
  const controlledFields = formattedServiceConfigOptions.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  const displayNameOption = watch('displayNameOption');

  /*
    * Since this form fetches data within it and has a non-traditional render cycle, we need to
    * invoke `handleAutoSave` manually when things change instead of relying on handleNodeFormAutoSave
    * like the other forms
    */
  const debouncedAutoSave = useDebouncedFunction<FormState>({
    fn: formData => {
      handleAutoSave(formData);
      dispatch(updateServiceActionValidation({
        publicId: focusedAction.publicId,
        data: [],
      }));
    },
  });

  const debouncedAutoSaveOnError = useDebouncedFunction<FieldErrors<FormState>>({
    fn: formErrors => {
      const errorMessages = formErrors?.formattedServiceConfigOptions?.map(
        e => `${displayNameOption?.value}: ${e.value?.message}`,
      );
      dispatch(updateServiceActionValidation({
        publicId: focusedAction.publicId,
        data: errorMessages,
      }));
    },
  });

  const submitAutoSave = () => handleSubmit(debouncedAutoSave, debouncedAutoSaveOnError)();

  useEffect(() => {
    if (controlledFields.length < 1 && serviceConfig) {
      let initialRecurringWebhook: ServiceOption = null;

      const {
        credentialOwnerOption: initCredOwnerOpt,
        formattedServiceConfigOptions: initSvcConfigOpts,
      } = convertServiceConfigOption(
        serviceConfig,
        focusedAction.options,
        focusedAction.service.credentialOwner,
      );

      serviceOptions.forEach(option => {
        /*
          Here we direct and handle action options for specific sections:
          1. handle recurring webhook separately
        */
        if (WEBHOOK_KEY === option.key) {
          initialRecurringWebhook = option;
        }
      });
      const displayNameIndex = initSvcConfigOpts.findIndex(
        option => option.key === DISPLAY_NAME_OPTION_KEY,
      );
      if (displayNameIndex >= 0) {
        const displayNameOptions: Array<FormattedServiceConfigOption> = initSvcConfigOpts.splice(
          displayNameIndex, 1,
        );
        setValue('displayNameOption', displayNameOptions[0] as DisplayNameOption);
      } else {
        const serviceConfigOption = serviceConfig.serviceConfigOptions.find(
          option => option.slug === DISPLAY_NAME_OPTION_KEY,
        );
        setValue('displayNameOption', {
          serviceConfigOptionId: serviceConfigOption?.serviceConfigOptionId,
          isAdding: false,
          value: service.name,
          defaultValue: service.name,
          isUsingDefault: false,
          defaultLevel: DefaultLevel.ALLOY,
          key: DISPLAY_NAME_OPTION_KEY,
          publicId: generatePublicId('actionoption'),
        } as DisplayNameOption);
      }

      setValue('recurringWorkflow', initialRecurringWebhook);
      setValue('credentialOwnerOption', initCredOwnerOpt);
      replaceServiceConfigOption(initSvcConfigOpts);
    }
  }, [
    serviceOptions, isServiceDataFetched, serviceConfig, service.name, focusedAction,
    replaceServiceConfigOption, setValue,
  ]);

  const serviceConfigOptionsMapping = serviceConfig?.serviceConfigOptions?.reduce(
    (acc, option) => {
      acc[option.serviceConfigOptionId] = option;
      return acc;
    }, {},
  );

  const handlingDescription = failureHandlingModeValue === 'full-threshold'
    ? failureHandlingDescriptionFullThresholdMode : failureHandlingDescriptionBestEffortMode;

  return (
    <StyledLocalSettingContainer>
      <Box gap={2} justifyContent="flex-start" alignItems="flex-start">
        <Box
          direction="column"
          gap={2}
          justifyContent="flex-start"
          style={{ padding: 'var(--cl-space-0) var(--cl-space-4)' }}
        >
          <Controller
            control={control}
            name="displayNameOption.value"
            rules={{ validate: value => (value?.length >= 1 || 'Display Name cannot be empty.' as const) }}
            render={({ field: { onChange, value } }) => (
              <Box data-dd-action-name="service-local-settings-display-name">
                <TextField
                  label="Display Name"
                  type="text"
                  helpText="Changes the name that is displayed for this service in the workflow node"
                  error={errors.displayNameOption?.value?.message}
                  value={value}
                  onChange={e => {
                    if (e.target.value !== '') {
                      onChange(e);
                      submitAutoSave();
                    } else {
                      addSnackBar({
                        title: 'Display Name cannot be empty.',
                        type: 'danger',
                      });
                    }
                  }}
                />
              </Box>
            )}
          />

          {isOnboardingWorkflow
            && recurringWorkflowsByService[getActionName(focusedAction)]
            && (
              <Controller
                name="recurringWorkflow"
                control={control}
                render={({ field: { onChange } }) => (
                  <RecurringWebhookContainer
                    initialSelectedRecurringWorkflow={serviceOptions.find(
                      option => option.key === WEBHOOK_KEY)}
                    recurringWorkflowOptions={
                      recurringWorkflowsByService[getActionName(focusedAction)]
                    }
                    onChange={e => {
                      onChange(e);
                      submitAutoSave();
                    }}
                  />
                )}
              />
            )}
        </Box>
        <Box
          direction="column"
          gap={2}
          style={{ padding: 'var(--cl-space-0) var(--cl-space-4) var(--cl-space-1)' }}
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <Controller
            name="serviceFailureDefaultOption"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MultiSelect
                isMulti={false}
                label="Service Failure Handling - if data is missing:"
                helpText={handlingDescription}
                onChange={e => {
                  onChange(e);
                  submitAutoSave();
                }}
                options={serviceFailureOptions}
                selectedOptions={value}
              />
            )}
          />
          <HelpText>
            Controls what action to take if the Service fails to run
          </HelpText>
        </Box>
      </Box>
      <Separator />
      {serviceConfig && (
        <ServiceConfigOption
          mode={ServiceConfigModalMode.NODE}
          serviceConfigOptionsMapping={serviceConfigOptionsMapping}
          formattedServiceConfigOptions={controlledFields}
          watch={watch}
          errors={errors}
          control={control}
          setValue={setValue}
          trigger={trigger}
          appendServiceConfigOption={appendServiceConfigOption}
          removeServiceConfigOption={removeServiceConfigOption}
          selectedAlloyOptions={serviceConfig.selectedAlloyOptions}
          selectedCustomerOptions={serviceConfig.selectedCustomerOptions}
          isAddingOption={isAddingOption}
          setIsAddingOption={
            isAdding => {
              setIsAddingOption(isAdding);
              if (isAdding) {
                dispatch(setServiceActionOptionPublicIdInEditing('true'));
              } else {
                dispatch(setServiceActionOptionPublicIdInEditing(null));
              }
            }
          }
          serviceName={service.name}
          serviceId={serviceId}
          submitAutoSave={submitAutoSave}
          onlySandboxEnabled={!productionEnabled && sandboxEnabled}
        />
      )}
    </StyledLocalSettingContainer>
  );
}
